import { BehaviorHandler } from './types'

/**
 * RJF05 の Cutter 船（Workspace/Cutter: Quenty 製 qShipManager）の待機時の
 * 波の揺れ。rjf05.ts が 800 行を超えたため、ミッション内の独立したギミック
 * としてファイル分割した（挙動は分割前と同一。近似の方針は runShipWaves の
 * doc コメントを参照）。
 */

/**
 * Cutter 船を構成するパーツの固有名接頭辞。シーンにはモデル概念が無いため
 * 前方一致で収集する（実シーンで該当する全 180 個が船域内にあることを確認済み）。
 */
const SHIP_PART_NAME_PREFIXES: ReadonlyArray<string> = [
  'LineSegment',
  'qBoatSeat',
  'Sail',
  'BlobbyblobBuild_',
  'Wedge',
  'Mast',
]

/** 波の振幅（Lua: Configuration.AmplitudeOfWaves = 2.5）。 */
const WAVE_AMPLITUDE = 2.5

/** 揺れ更新の周期（Lua: wait(0.1) の駆動ループ）。 */
const WAVE_STEP_SECONDS = 0.1

/** 1 更新あたりの位相の進み（Lua: Count += 0.1 * Delta、Delta ≒ 0.1 秒）。 */
const WAVE_PHASE_PER_STEP = 0.1 * WAVE_STEP_SECONDS

/**
 * Cutter 船の波による揺れ（Lua: qShipManager の WaveCoroutine）。
 * 原典はサーバー起動直後から PhysicsManager.Active = true で、操縦者の有無に
 * かかわらず 0.1 秒周期で WaveHeight = sin(Count) × 2.5 を BodyPosition へ
 * 与え、船全体が振幅 2.5 studs・周期約 63 秒でゆっくり上下する。
 *
 * プレビューでの近似・意図的な差異:
 * - VehicleSeat による操縦（前進・旋回・傾き・沈没）はプレビューに座席の
 *   概念が無いため移植しない（待機時の揺れのみ再現する）。
 * - 揺れに伴う微小ピッチ（MaxShipYawInRadians = π/120 ≒ 1.5 度）は多パーツ
 *   モデルの一斉回転が必要なわりに視認性が低いため省略する。
 */
export const runShipWaves: BehaviorHandler = async (ctx) => {
  const { world } = ctx
  const shipIds: string[] = []
  for (const state of world.getState().values()) {
    if (
      SHIP_PART_NAME_PREFIXES.some((prefix) => state.name.startsWith(prefix))
    ) {
      shipIds.push(state.id)
    }
  }
  if (shipIds.length === 0) {
    return
  }
  // 位相 0 開始（Lua は tick() 起点の任意位相なので開始位相に意味はない）。
  let phase = 0
  let height = 0
  while (!ctx.signal.aborted) {
    phase += WAVE_PHASE_PER_STEP
    const nextHeight = Math.sin(phase) * WAVE_AMPLITUDE
    const dy = nextHeight - height
    height = nextHeight
    for (const id of shipIds) {
      if (world.exists(id)) {
        world.moveBy(id, { x: 0, y: dy, z: 0 })
      }
    }
    await ctx.wait(WAVE_STEP_SECONDS)
  }
}
